import { useTheme } from '@emotion/react';
import { Edit, Receipt } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useQueryClient } from '@tanstack/react-query';
import React, { Fragment, Suspense, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import CommonDialog from '../../../common/CommonDialog';
import { FormatNumberForKm } from '../../../components/FormatNumber/FormatNumber';
import { SHOP_CREATED } from '../../../config/dataService/constants';
import { DETAIL_STATUS, DetailStatusChip } from '../../../common/DetailStatusChip';
import { deleteInvoice, sendInvoice } from '../../../redux/Invoice/action';
import { businessInvoicesKeys } from '../../../services/business/invoices/invoices.hooks';
import { reviewRequestKeys } from '../../../services/business/reviewRequest/reviewRequest.hooks.js';
import { useGetShopConfig, useGetShopTaxes } from '../../../services/core/settings';
import LineTaxCell from '../../../common/Tax/LineTaxCell';
import { DOCUMENT_TAX_TYPE, isNewTaxVersion, LINE_TAX_KIND, recordHasPartsOrPackages, recordPartGrossAmount } from '../../../utility/helpers/taxCalculation';
import { useProvinceTaxGroup } from '../../../utility/hooks/useProvinceTaxGroup';
import {
    INVENTORY_MANAGE_TYPE,
    INVENTORY_TYPES,
    MILEAGE_UNIT,
    normalizeWorkOrderStatus,
    PART_TYPES,
    PRICING_TYPES,
    WORK_ORDER_STATUS,
} from '../../../utility/Constant';
import { NumericEngine } from '../../../utility/helpers/numericEngine';
import { shouldShowPerServiceSubtotalRow } from '../../../utility/helpers/shouldShowPerServiceSubtotalRow';
import LinkedDocumentLink, { getEstimateDetailUrl } from '../../../common/LinkedDocumentLink';
import LinkIcon from '../../../utility/Svg/LinkIcon';
import PartBrandSvg from '../../../utility/Svg/PartBrandSvg';
import PartCategorySvg from '../../../utility/Svg/PartCategorySvg';
import ResendSvg from '../../../utility/Svg/ResendSvg';
import SaveAndSendSvg from '../../../utility/Svg/SaveAndSendSvg';
import SuccessMessageModal from '../../Common/SuccessMessage';
import UpdateCustomerModal from '../../Customers/Profile/UpdateCustomerModal';
import AddInventoryModal from '../../Inventory/AddInventory';
import BatchViewModal from '../../Inventory/BatchViewModal';
import DetailPageHeaderInfoColumns from '../invoiceCommonComponents/DetailPageHeaderInfoColumns';
import InvoiceFeesTable from '../invoiceCommonComponents/InvoiceFeesTable';
import InvoicePaymenetDetails from '../invoiceCommonComponents/InvoicePaymenetDetails';
import InvoiceTermsWarranty from '../invoiceCommonComponents/InvoiceTermsWarranty';
import ShopSuppliesTable from '../invoiceCommonComponents/ShopSuppliesTable';
import { toast } from 'sonner';
import InvoiceRecommandationSection from '../invoiceCommonComponents/InvoiceRecommandationSection.jsx';
import AddCustomerModalV4 from '../../../common/AddCustomerModalV4/index.jsx';
import { openPdfUrl } from '../../../utility/helpers/fileDownload';

const InvoiceDraft = ({ invoiceId, invoiceData = {}, customerData, setCustomerData, setShowLoader, showLoader, fetchApi }) => {
    const provinceTaxGroup = useProvinceTaxGroup();
    const documentHasPartsOrPackages = recordHasPartsOrPackages(invoiceData);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const [openSuccessModal, setOpenSuccessModal] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState('');

    const [selectedInventoryId, setSelectedInventoryId] = useState(null);
    const [batchNumber, setBatchNumber] = useState(null);
    const [openPartModel, setOpenpartModel] = useState(false);

    const [openBatchSelectionModal, setBatchSelectionModal] = useState(false);
    const [inventoryOption, setInventoryOption] = useState(null);

    const [isDeleteModal, setIsDeleteModal] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [openEditCustomer, setOpenEditCustomer] = useState(false);
    const [primaryEmailIndex, setPrimaryEmailIndex] = useState(0);
    const [addressValues, setAddressValue] = useState({
        street_number: '',
        city: '',
        province: '',
        postal: '',
        country: '',
        provinceId: '',
        cityId: '',
    });

    const theme = useTheme();
    const { data: shopConfigData, isLoading: isConfigLoading, error: configError } = useGetShopConfig();
    const { data: shopTaxesData } = useGetShopTaxes();

    const showLineTax = isNewTaxVersion(invoiceData);

    const mileageUnit = shopConfigData?.config?.mileageUnit === MILEAGE_UNIT.MILES ? MILEAGE_UNIT.MILES : MILEAGE_UNIT.KM;

    // const queryClient = useQueryClient();
    function handleClick(event) {
        event.preventDefault();
        console.info('You clicked a breadcrumb.');
    }

    // const deleteDraftInvoice = async () => {
    //     let data = await dispatch(deleteInvoice(invoiceId));

    //     if (data) {
    //         queryClient.invalidateQueries({ queryKey: businessInvoicesKeys.all, refetchType: 'all' });
    //         navigate('/invoice/drafts');
    //     }
    // };

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [open, setOpen] = React.useState(false);
    const open1 = Boolean(anchorEl);
    const handleClick1 = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose1 = () => {
        setAnchorEl(null);
    };

    const handlePrintPdf = () => {
        openPdfUrl(invoiceData?.pdfUrl);
        handleClose1();
    };

    const getLinkedEstimateUrl = () => getEstimateDetailUrl(invoiceData?.linkedEstimateId);

    /** Work order detail url by status — the work order it was converted from (spec §6.2). */
    const getLinkedWorkOrderUrl = () => {
        const workOrder = Array.isArray(invoiceData?.linkedWorkOrderId) ? invoiceData?.linkedWorkOrderId?.[0] : invoiceData?.linkedWorkOrderId;
        if (!workOrder?._id) return '#';

        return `/work-orders/${normalizeWorkOrderStatus(workOrder?.status) || WORK_ORDER_STATUS.OPEN}/${workOrder._id}`;
    };

    /** The work order this invoice was created from, if any — the API returns a single object. */
    const sourceWorkOrder = Array.isArray(invoiceData?.linkedWorkOrderId) ? invoiceData?.linkedWorkOrderId?.[0] : invoiceData?.linkedWorkOrderId;

    const hasCustomerEmail = !!invoiceData?.customer?.email;
    const customerId = invoiceData?.customer?._id;
    const handleSend = async () => {
        try {
            setShowLoader(true);
            const data = await dispatch(sendInvoice(invoiceId));
            if (data) {
                toast.success('Invoice was successfully emailed to customer');

                handleClose1();
                fetchApi();
                // setTimeout(() => {

                // }, 1500);
            } else {
                // the reason was already shown by the response interceptor
                setShowLoader(false);
            }
        } catch (error) {
            setShowLoader(false);
        }
    };

    const serviceSubtotalCache = new WeakMap();
    const selectServiceSubtotal = (service) => {
        if (!service) return '0';

        if (serviceSubtotalCache.has(service)) {
            return serviceSubtotalCache.get(service);
        }
        let subtotal = '0';

        // FIXED service pricing
        if (service.pricingType === PRICING_TYPES.FXIED) {
            subtotal = service.fixedPrice || '0';
        }

        // LABOUR & PARTS service pricing
        if (service.pricingType === PRICING_TYPES.LABOUR) {
            const laborAmount = service?.labor?.totalAmount || '0';

            // qty × (cost + per-unit fee): part.totalAmount is qty × cost only, the fee is billed on
            // its own row and must be counted here too (see recordPartGrossAmount).
            const partsAmount = (service.parts || []).reduce((acc, part) => NumericEngine.add(acc, String(recordPartGrossAmount(part))), '0');

            subtotal = NumericEngine.add(laborAmount, partsAmount);
        }

        serviceSubtotalCache.set(service, subtotal);

        return subtotal;
    };

    return (
        <Box>
            <Box
                padding={'32px 24px'}
                sx={{
                    bgcolor: 'background.main',
                    width: 'fit-content',
                    minWidth: '100%',
                }}
            >
                {' '}
                <Grid container sx={{ padding: '16px 0px' }} gap={'8px'}>
                    <Grid item lg={12} md={12} sm={12} xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div role="presentation" onClick={handleClick}>
                            <Breadcrumbs aria-label="breadcrumb">
                                <Link
                                    to="/invoice/all"
                                    style={{
                                        color: 'black',
                                        opacity: '0.6',
                                        textDecoration: 'none',
                                    }}
                                >
                                    Invoices
                                </Link>
                                <Link
                                    to="/invoice/drafts"
                                    style={{
                                        color: 'black',
                                        opacity: '0.6',
                                        textDecoration: 'none',
                                    }}
                                >
                                    Drafts
                                </Link>
                                <Link to="/invoice/drafts" style={{ color: 'black', textDecoration: 'none' }}>
                                    {invoiceData?.invoiceNumber}
                                </Link>
                            </Breadcrumbs>
                        </div>
                        {/* Linked documents: the estimate first, the work order beneath it. */}
                        <Stack alignItems="flex-end" spacing={0.5}>
                            {invoiceData?.linkedEstimateId?._id && (
                                <Link to={getLinkedEstimateUrl()} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <LinkIcon color="#002394" />
                                    <Typography
                                        variant="body1"
                                        component="span"
                                        sx={{
                                            color: '#002394',
                                            fontFamily: 'Roboto',
                                            fontSize: '16px',
                                            fontStyle: 'normal',
                                            fontWeight: 400,
                                            lineHeight: '150%',
                                            letterSpacing: '0.15px',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        Linked to {invoiceData?.linkedEstimateId?.estimateNumber || ''}
                                    </Typography>
                                </Link>
                            )}

                            {sourceWorkOrder?._id && <LinkedDocumentLink to={getLinkedWorkOrderUrl()} documentNumber={sourceWorkOrder?.workOrderNumber} />}
                        </Stack>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', fontWeight: '500' }}>
                            {invoiceData?.invoiceNumber}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={2} justifyContent="end">
                    <Grid item>
                        <Stack direction={'row'} spacing={2}>
                            <Button
                                variant="contained"
                                color="secondary"
                                className="btn-text-size-manage"
                                startIcon={<Edit />}
                                onClick={() => {
                                    navigate(`/invoice/edit/${invoiceId}`);
                                }}
                                style={{
                                    fontWeight: 500,
                                    lineHeight: '24px',
                                    letterSpacing: '0.04px',
                                    fontSize: '14px',
                                    height: '42px',
                                }}
                            >
                                Edit Invoice
                            </Button>

                            {/* A draft is not sent to the customer — no Email to Customer here (it appears once finalized). */}

                            <Button
                                variant="contained"
                                // color="secondary"
                                color="greyColor"
                                id="demo-positioned-button"
                                aria-controls={open ? 'demo-positioned-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                onClick={handleClick1}
                                style={{
                                    width: '187px',
                                    fontWeight: 500,
                                    lineHeight: '24px',
                                    letterSpacing: '0.4px',
                                    fontSize: '14px',
                                    height: '42px',
                                    color: '#000000',
                                    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.25)',
                                }}
                            >
                                more actions
                                <ExpandMoreIcon style={{ marginLeft: '8px' }} />
                            </Button>
                            <Menu
                                style={{ top: 3 }}
                                id="demo-positioned-menu"
                                aria-labelledby="demo-positioned-button"
                                anchorEl={anchorEl}
                                open={open1}
                                onClose={handleClose1}
                                sx={{
                                    // MORE ACTIONS menu: the button's width, sitting directly under it.
                                    '& .MuiPaper-root': { width: '187px', minWidth: 'unset!important' },
                                    '& .MuiMenuItem-root': { padding: '6px 12px!important', width: '100%' },
                                }}
                            >
                                {' '}
                                {invoiceData?.pdfUrl && (
                                    <MenuItem onClick={handlePrintPdf}>
                                        <Receipt style={{ marginRight: '8px' }} /> View PDF
                                    </MenuItem>
                                )}
                                <MenuItem
                                    onClick={() => {
                                        navigate(`/customers/${invoiceData?.customer?._id}/${invoiceData?.customer?.createdFrom}`);
                                    }}
                                >
                                    <VisibilityIcon style={{ marginRight: '8px' }} />
                                    View Customer
                                </MenuItem>
                                {invoiceData?.isEmailSent && (
                                    <MenuItem
                                        onClick={() => {
                                            handleSend();
                                        }}
                                    >
                                        <ResendSvg />
                                        <span style={{ marginLeft: '14px', width: '40px' }}>Resend</span>
                                    </MenuItem>
                                )}
                                <MenuItem
                                    onClick={() => {
                                        // deleteDraftInvoice();
                                        setIsDeleteModal(true);
                                        handleClose1();
                                    }}
                                >
                                    {' '}
                                    <DeleteIcon style={{ marginRight: '8px', color: '#D32F2F' }} /> <span style={{ color: '#D32F2F' }}> Delete Invoice </span>
                                </MenuItem>
                            </Menu>
                        </Stack>
                    </Grid>
                </Grid>
                <Grid container padding={'24px'} boxShadow={2} margin={'32px 0'} gap={2} sx={{ bgcolor: 'white' }}>
                    <Grid container item spacing={2} justifyContent="space-between" paddingRight={2}>
                        <Grid item lg={8} md={12} sm={12} xs={12}>
                            <Typography variant="h3" fontWeight={400}>
                                Invoice{' '}
                            </Typography>
                            <Stack direction="row" justifyContent={'flex-start'} gap={2} mt={1} sx={{ flexWrap: 'wrap' }}>
                                <DetailStatusChip status={DETAIL_STATUS.DRAFT} />
                            </Stack>
                        </Grid>

                        <Grid item>
                            <Stack spacing={0}>
                                <Typography variant="body1" fontWeight={'500'} fontSize={'24px'} lineHeight={'133.33%'}>
                                    {invoiceData?.invoiceNumber}
                                </Typography>
                                {invoiceData?.serviceAdvisor?.name && (
                                    <Typography variant="body1" fontWeight={400} lineHeight={'150%'} mt={1}>
                                        Service Advisor: {invoiceData?.serviceAdvisor?.name}
                                    </Typography>
                                )}

                                {invoiceData?.serviceTechnician?.name && (
                                    <Typography variant="body1" fontWeight={400} lineHeight={'150%'} mt={0.5}>
                                        Technician: {invoiceData?.serviceTechnician?.name}
                                    </Typography>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                    <Divider style={{ width: '100%' }} />

                    <Grid container item justifyItems={'space-between'}>
                        <DetailPageHeaderInfoColumns
                            invoiceDate={invoiceData?.invoiceDate}
                            customer={invoiceData?.customer}
                            customerPO={invoiceData?.customerPO}
                            vehicle={invoiceData?.vehicle}
                            mileageUnit={mileageUnit}
                            odoDroppedOff={invoiceData?.odoDroppedOff}
                            odoReturned={invoiceData?.odoReturned}
                            mileageInDate={invoiceData?.appointmentDroppedOffDate}
                            mileageOutDate={invoiceData?.appointmentCompletionDate}
                        />

                        {invoiceData?.note && (
                            <Grid item xs={12} mt={2}>
                                <Typography
                                    style={{
                                        color: 'rgba(0, 0, 0, 0.87)',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        lineHeight: '150%',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    Internal Shop Memo
                                </Typography>
                                <Typography
                                    style={{
                                        color: 'rgba(0, 0, 0, 0.65)',
                                        marginTop: '8px',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '24px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    {invoiceData?.note}
                                </Typography>
                            </Grid>
                        )}

                        {invoiceData &&
                            invoiceData?.services?.length > 0 &&
                            invoiceData?.services?.map((item, index) => {
                                const serviceSubtotal = selectServiceSubtotal(item) ?? '0';

                                return (
                                    <>
                                        <Grid
                                            container
                                            item
                                            /*style={{
                                                            backgroundColor: "#c5d0da",
                                                            color: "black",
                                                            marginTop: "20px",
                                                          }}*/
                                            sx={{
                                                bgcolor: 'secondary.main',
                                                color: 'white',
                                                mt: '32px',
                                                borderTopLeftRadius: '4px',
                                                borderTopRightRadius: '4px',
                                            }}
                                        >
                                            <Grid item xs={12}>
                                                <Typography
                                                    sx={{
                                                        p: '16px',
                                                        fontSize: '20px',
                                                        fontWeight: 500,
                                                        lineHeight: '32px',
                                                        letterSpacing: '0.15px',
                                                    }}
                                                >
                                                    {item?.serviceName || item?.serviceId?.name}
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        {/* Service Table */}

                                        <Grid container item>
                                            <Grid item xl={12} md={12} sm={12} xs={12}>
                                                <TableContainer sx={{ maxHeight: 600 }}>
                                                    <Table
                                                        aria-label="service table"
                                                        padding="16px"
                                                        sx={{
                                                            width: '100%',
                                                            tableLayout: 'fixed',
                                                            '& .MuiTableCell-root': {
                                                                borderBottom: '1px solid rgba(0,0,0,0.12)',
                                                                verticalAlign: 'middle',
                                                                color: '#212121',
                                                            },
                                                        }}
                                                    >
                                                        <colgroup>
                                                            <col style={{ width: 56 }} />
                                                            <col />
                                                            <col style={{ width: 96 }} />
                                                            <col style={{ width: 90 }} />
                                                            <col style={{ width: 100 }} />
                                                            {showLineTax && <col style={{ width: 52 }} />}
                                                        </colgroup>

                                                        {/* Table Head */}
                                                        <TableHead
                                                            sx={{
                                                                backgroundColor: '#F8F8F8',
                                                                height: '40px',
                                                                borderBottom: 'none',
                                                            }}
                                                        >
                                                            <TableRow
                                                                sx={{
                                                                    height: '40px',
                                                                    borderBottom: 'none',
                                                                    '& > th, & > td': {
                                                                        px: '12px',
                                                                    },
                                                                }}
                                                            >
                                                                {/* No */}
                                                                <TableCell
                                                                    sx={{
                                                                        width: '56px',
                                                                        fontSize: '14px',
                                                                        borderBottom: 'none',
                                                                    }}
                                                                    style={{
                                                                        paddingTop: '0',
                                                                        paddingBottom: '0',
                                                                        paddingLeft: '16px',
                                                                        paddingRight: '0',
                                                                    }}
                                                                >
                                                                    No.
                                                                </TableCell>

                                                                {/* Description */}
                                                                <TableCell
                                                                    sx={{
                                                                        fontSize: '14px',
                                                                        paddingTop: '0',
                                                                        paddingBottom: '0',
                                                                        borderBottom: 'none',
                                                                    }}
                                                                    style={{
                                                                        height: '40px',
                                                                        paddingLeft: '0',
                                                                        paddingRight: '10px',
                                                                    }}
                                                                >
                                                                    Description
                                                                </TableCell>

                                                                {/* Quantity */}
                                                                <TableCell
                                                                    // align="left"
                                                                    sx={{
                                                                        width: '96px',
                                                                        fontSize: '14px',
                                                                        borderBottom: 'none',
                                                                    }}
                                                                    style={{ padding: '0 10px' }}
                                                                >
                                                                    Quantity
                                                                </TableCell>

                                                                {/* Price */}
                                                                <TableCell
                                                                    // align="center"
                                                                    sx={{
                                                                        width: '120px',
                                                                        fontSize: '14px',
                                                                        borderBottom: 'none',
                                                                    }}
                                                                    style={{ padding: '0 10px' }}
                                                                >
                                                                    Price
                                                                </TableCell>

                                                                {/* Amount */}
                                                                <TableCell
                                                                    align={showLineTax ? 'left' : 'right'}
                                                                    sx={{
                                                                        width: '120px',
                                                                        fontSize: '14px',
                                                                        borderBottom: 'none',
                                                                    }}
                                                                    style={{ padding: '0 10px' }}
                                                                >
                                                                    Amount
                                                                </TableCell>

                                                                {/* Tax */}
                                                                {showLineTax && (
                                                                    <TableCell
                                                                        align="center"
                                                                        sx={{
                                                                            width: '80px',
                                                                            fontSize: '14px',
                                                                            paddingTop: '0',
                                                                            paddingBottom: '0',
                                                                            borderBottom: 'none',
                                                                        }}
                                                                        style={{ paddingRight: '16px', paddingLeft: '10px' }}
                                                                    >
                                                                        Tax
                                                                    </TableCell>
                                                                )}
                                                            </TableRow>
                                                        </TableHead>

                                                        {/* Table Body */}
                                                        <TableBody>
                                                            {/* Labour Description */}
                                                            {item?.description.trim() && item?.pricingType === PRICING_TYPES.LABOUR && (
                                                                <TableRow>
                                                                    <TableCell
                                                                        style={{
                                                                            padding: '6px 0 6px 16px',
                                                                        }}
                                                                    />

                                                                    <TableCell
                                                                        style={{
                                                                            padding: '6px 10px 6px 0',
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize: '16px',
                                                                                fontWeight: 400,
                                                                                lineHeight: '143%',
                                                                            }}
                                                                        >
                                                                            {item?.description}
                                                                        </Typography>
                                                                    </TableCell>

                                                                    <TableCell style={{ padding: '6px 10px' }} />
                                                                    <TableCell style={{ padding: '6px 10px' }} />
                                                                    <TableCell style={{ padding: '6px 10px' }} />
                                                                    {showLineTax && <TableCell style={{ padding: '6px 16px 6px 10px' }} />}
                                                                </TableRow>
                                                            )}

                                                            {/* Labour Row */}
                                                            {item?.pricingType === PRICING_TYPES.LABOUR && item?.labor && item?.labor?.qty > 0 ? (
                                                                <TableRow key={item?.labor?.qty}>
                                                                    <TableCell
                                                                        style={{
                                                                            fontSize: '16px',
                                                                            padding: '6px 0 6px 16px',
                                                                        }}
                                                                    >
                                                                        1
                                                                    </TableCell>

                                                                    <TableCell
                                                                        style={{
                                                                            padding: '6px 10px 6px 0',
                                                                            fontSize: '16px',
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize: '16px',
                                                                                fontWeight: 400,
                                                                                lineHeight: '143%',
                                                                            }}
                                                                        >
                                                                            Labour
                                                                        </Typography>
                                                                    </TableCell>

                                                                    <TableCell
                                                                        style={{
                                                                            padding: '6px 10px',
                                                                            fontSize: '16px',
                                                                        }}
                                                                    >
                                                                        {item?.labor?.qty}
                                                                    </TableCell>

                                                                    <TableCell
                                                                        style={{
                                                                            padding: '6px 10px',
                                                                            fontSize: '16px',
                                                                        }}
                                                                    >
                                                                        {item?.labor?.ratePerHour
                                                                            ? NumericEngine.format(item?.labor?.ratePerHour || 0, {
                                                                                  formatCurrency: true,
                                                                                  trailingZeros: true,
                                                                              })
                                                                            : '$0.00'}
                                                                    </TableCell>

                                                                    {/* Amount */}
                                                                    <TableCell
                                                                        align={showLineTax ? 'left' : 'right'}
                                                                        style={{
                                                                            padding: '6px 10px',
                                                                            fontSize: '16px',
                                                                            fontWeight: 400,
                                                                        }}
                                                                    >
                                                                        <Typography fontWeight={400} fontSize={16}>
                                                                            {item?.labor?.totalAmount
                                                                                ? NumericEngine.format(item?.labor?.totalAmount || 0, {
                                                                                      formatCurrency: true,
                                                                                      trailingZeros: true,
                                                                                  })
                                                                                : '$0.00'}
                                                                        </Typography>
                                                                    </TableCell>

                                                                    {/* Tax */}
                                                                    {showLineTax && (
                                                                        <TableCell
                                                                            align="center"
                                                                            style={{
                                                                                padding: '6px 16px 6px 10px',
                                                                                fontSize: '16px',
                                                                            }}
                                                                        >
                                                                            <LineTaxCell
                                                                                taxesBreakdown={item?.labor?.taxesBreakdown}
                                                                                lineAmount={item?.labor?.totalAmount}
                                                                                documentType={DOCUMENT_TAX_TYPE.INVOICE}
                                                                                shopTaxesData={shopTaxesData}
                                                                                provinceTaxGroup={provinceTaxGroup}
                                                                                documentHasPartsOrPackages={documentHasPartsOrPackages}
                                                                                kind={LINE_TAX_KIND.LABOUR}
                                                                                editable={false}
                                                                                discount={invoiceData?.discount}
                                                                                subTotal={invoiceData?.subTotal}
                                                                            />
                                                                        </TableCell>
                                                                    )}
                                                                </TableRow>
                                                            ) : item?.pricingType === PRICING_TYPES.FXIED ? (
                                                                /* Fixed Price Row */
                                                                <TableRow key={item?.labor?.qty}>
                                                                    <TableCell
                                                                        style={{
                                                                            fontSize: '16px',
                                                                            padding: '6px 0 6px 16px',
                                                                        }}
                                                                    >
                                                                        1
                                                                    </TableCell>

                                                                    <TableCell
                                                                        style={{
                                                                            padding: '6px 10px 6px 0',
                                                                            fontSize: '16px',
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize: '16px',
                                                                                fontWeight: 400,
                                                                                lineHeight: '143%',
                                                                            }}
                                                                        >
                                                                            {item?.pricingType === PRICING_TYPES.FXIED && item?.description
                                                                                ? item?.description
                                                                                : item?.pricingType === PRICING_TYPES.FXIED
                                                                                  ? 'Fixed Price'
                                                                                  : item?.serviceName || item?.serviceId?.name}
                                                                        </Typography>
                                                                    </TableCell>

                                                                    <TableCell style={{ padding: '6px 10px' }} />

                                                                    <TableCell style={{ padding: '6px 10px' }} />

                                                                    {/* Amount */}
                                                                    <TableCell
                                                                        align={showLineTax ? 'left' : 'right'}
                                                                        style={{
                                                                            padding: '6px 10px',
                                                                            fontSize: '16px',
                                                                        }}
                                                                    >
                                                                        <Typography fontWeight={400} fontSize={16}>
                                                                            {item?.fixedPrice
                                                                                ? NumericEngine.format(item?.fixedPrice || 0, {
                                                                                      formatCurrency: true,
                                                                                      trailingZeros: true,
                                                                                  })
                                                                                : '$0.00'}
                                                                        </Typography>
                                                                    </TableCell>

                                                                    {/* Tax */}
                                                                    {showLineTax && (
                                                                        <TableCell
                                                                            align="center"
                                                                            style={{
                                                                                padding: '6px 16px 6px 10px',
                                                                                fontSize: '16px',
                                                                            }}
                                                                        >
                                                                            <LineTaxCell
                                                                                taxesBreakdown={item?.taxesBreakdown}
                                                                                lineAmount={item?.fixedPrice}
                                                                                documentType={DOCUMENT_TAX_TYPE.INVOICE}
                                                                                shopTaxesData={shopTaxesData}
                                                                                provinceTaxGroup={provinceTaxGroup}
                                                                                documentHasPartsOrPackages={documentHasPartsOrPackages}
                                                                                kind={LINE_TAX_KIND.PACKAGE}
                                                                                editable={false}
                                                                                discount={invoiceData?.discount}
                                                                                subTotal={invoiceData?.subTotal}
                                                                            />
                                                                        </TableCell>
                                                                    )}
                                                                </TableRow>
                                                            ) : null}

                                                            {item?.parts &&
                                                                item?.parts?.length > 0 &&
                                                                item?.parts?.map((row, index) => {
                                                                    const showFeeCell = row?.fee?.name && row.feeAmount > 0;
                                                                    return (
                                                                        <Fragment key={index}>
                                                                            <TableRow>
                                                                                {/* No */}
                                                                                <TableCell
                                                                                    style={{
                                                                                        padding: '6px 0 6px 16px',
                                                                                        fontSize: '16px',
                                                                                        borderBottom: row?.lastDescription || showFeeCell ? 'none' : '',
                                                                                    }}
                                                                                >
                                                                                    {index + (item?.fixedPrice > 0 || item?.labor?.qty > 0 ? 2 : 1)}
                                                                                </TableCell>

                                                                                {/* Description (name + inline supplier/brand details) */}
                                                                                <TableCell
                                                                                    style={{
                                                                                        padding: '6px 12px 6px 0',
                                                                                        borderBottom: row?.lastDescription || showFeeCell ? 'none' : '',
                                                                                    }}
                                                                                >
                                                                                    <Box>
                                                                                        <Typography
                                                                                            sx={{
                                                                                                color: 'rgba(0, 0, 0, 0.87)',
                                                                                                fontSize: '16px',
                                                                                                fontWeight: 400,
                                                                                                letterSpacing: '0.17px',
                                                                                                lineHeight: '143%',
                                                                                                textDecoration:
                                                                                                    row.inventoryType === INVENTORY_MANAGE_TYPE.UNMANAGED
                                                                                                        ? 'none'
                                                                                                        : 'underline',
                                                                                                cursor:
                                                                                                    row.inventoryType === INVENTORY_MANAGE_TYPE.UNMANAGED
                                                                                                        ? 'default'
                                                                                                        : 'pointer',
                                                                                            }}
                                                                                            onClick={() => {
                                                                                                if (row.inventoryType === INVENTORY_MANAGE_TYPE.UNMANAGED) {
                                                                                                    return;
                                                                                                }
                                                                                                setSelectedInventoryId(row?.inventoryId || row?.inventory?._id);
                                                                                                setInventoryOption(row);
                                                                                                if (row?.inventoryBatches?.length > 1) {
                                                                                                    setBatchSelectionModal(true);
                                                                                                } else if (
                                                                                                    row?.inventoryBatches?.length > 0 &&
                                                                                                    row?.inventoryBatches[0]?.batchNumber
                                                                                                ) {
                                                                                                    setBatchNumber(
                                                                                                        row?.inventoryBatches[0]?.batchNumber ||
                                                                                                            row.batchNumber[0]
                                                                                                    );
                                                                                                    setOpenpartModel(true);
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            {row?.partName || row?.inventory?.part?.partName}
                                                                                        </Typography>

                                                                                        <Stack
                                                                                            direction="row"
                                                                                            // alignItems="center"
                                                                                            spacing={1.25}
                                                                                            // sx={{ marginTop: '4px' }}
                                                                                            divider={
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        width: '1px',
                                                                                                        height: '16px',
                                                                                                        bgcolor: 'rgba(0, 0, 0, 0.12)',
                                                                                                    }}
                                                                                                />
                                                                                            }
                                                                                        >
                                                                                            {(row?.partNumber || row?.tyreDimension) && (
                                                                                                <Stack
                                                                                                    direction="row"
                                                                                                    alignItems="center"
                                                                                                    spacing={0.5}
                                                                                                    mt={'4px'}
                                                                                                >
                                                                                                    <Typography
                                                                                                        component="span"
                                                                                                        sx={{
                                                                                                            fontSize: '16px',
                                                                                                            fontWeight: 400,
                                                                                                            letterSpacing: '0.17px',
                                                                                                            color: '#006CDD',
                                                                                                        }}
                                                                                                    >
                                                                                                        #
                                                                                                    </Typography>
                                                                                                    <Typography
                                                                                                        component="span"
                                                                                                        sx={{
                                                                                                            fontSize: '16px',
                                                                                                            fontWeight: 400,
                                                                                                            letterSpacing: '0.17px',
                                                                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                                                                        }}
                                                                                                    >
                                                                                                        {row?.part?.partsType === PART_TYPES.PARTS
                                                                                                            ? row?.partNumber
                                                                                                            : row?.tyreDimension}
                                                                                                    </Typography>
                                                                                                </Stack>
                                                                                            )}

                                                                                            {row?.supplier?.name && (
                                                                                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                                                                                    <PartBrandSvg />
                                                                                                    <Typography
                                                                                                        sx={{
                                                                                                            fontSize: '16px',
                                                                                                            fontWeight: 400,
                                                                                                            letterSpacing: '0.17px',
                                                                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                                                                        }}
                                                                                                    >
                                                                                                        {row.supplier.name}
                                                                                                    </Typography>
                                                                                                </Stack>
                                                                                            )}

                                                                                            {row?.partsBrand?.name && (
                                                                                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                                                                                    <PartCategorySvg />
                                                                                                    <Typography
                                                                                                        sx={{
                                                                                                            fontSize: '16px',
                                                                                                            fontWeight: 400,
                                                                                                            letterSpacing: '0.17px',
                                                                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                                                                        }}
                                                                                                    >
                                                                                                        {row.partsBrand.name}
                                                                                                    </Typography>
                                                                                                </Stack>
                                                                                            )}
                                                                                        </Stack>
                                                                                    </Box>
                                                                                </TableCell>

                                                                                {/* Quantity */}
                                                                                <TableCell
                                                                                    // align="left"
                                                                                    style={{
                                                                                        padding: '6px 10px',
                                                                                        fontSize: '16px',
                                                                                        borderBottom: row?.lastDescription || showFeeCell ? 'none' : '',
                                                                                    }}
                                                                                >
                                                                                    {row?.totalQty}
                                                                                </TableCell>

                                                                                {/* Price */}
                                                                                <TableCell
                                                                                    style={{
                                                                                        padding: '6px 10px',
                                                                                        fontSize: '16px',
                                                                                        borderBottom: row?.lastDescription || showFeeCell ? 'none' : '',
                                                                                    }}
                                                                                >
                                                                                    {row?.cost
                                                                                        ? NumericEngine.format(row?.cost || 0, {
                                                                                              formatCurrency: true,
                                                                                              trailingZeros: true,
                                                                                          })
                                                                                        : '$0.00'}
                                                                                </TableCell>

                                                                                {/* Amount */}
                                                                                <TableCell
                                                                                    align={showLineTax ? 'left' : 'right'}
                                                                                    style={{
                                                                                        padding: '6px 10px',
                                                                                        fontSize: '16px',
                                                                                        fontWeight: 400,
                                                                                        borderBottom: row?.lastDescription || showFeeCell ? 'none' : '',
                                                                                    }}
                                                                                >
                                                                                    <Typography fontWeight={400} fontSize={16}>
                                                                                        {row?.totalAmount
                                                                                            ? NumericEngine.format(row?.totalAmount || 0, {
                                                                                                  formatCurrency: true,
                                                                                                  trailingZeros: true,
                                                                                              })
                                                                                            : '$0.00'}
                                                                                    </Typography>
                                                                                </TableCell>

                                                                                {/* Tax */}
                                                                                {showLineTax && (
                                                                                    <TableCell
                                                                                        align="center"
                                                                                        style={{
                                                                                            padding: '6px 16px 6px 10px',
                                                                                            fontSize: '16px',
                                                                                            borderBottom: row?.lastDescription || showFeeCell ? 'none' : '',
                                                                                        }}
                                                                                    >
                                                                                        <LineTaxCell
                                                                                            taxesBreakdown={row?.taxesBreakdown}
                                                                                            // Tax base = qty × (cost + fee); totalAmount omits the fee.
                                                                                            lineAmount={recordPartGrossAmount(row)}
                                                                                            documentType={DOCUMENT_TAX_TYPE.INVOICE}
                                                                                            shopTaxesData={shopTaxesData}
                                                                                            provinceTaxGroup={provinceTaxGroup}
                                                                                            documentHasPartsOrPackages={documentHasPartsOrPackages}
                                                                                            kind={LINE_TAX_KIND.PARTS}
                                                                                            editable={false}
                                                                                            discount={invoiceData?.discount}
                                                                                            subTotal={invoiceData?.subTotal}
                                                                                        />
                                                                                    </TableCell>
                                                                                )}
                                                                            </TableRow>

                                                                            {/* Description Row */}
                                                                            {row?.lastDescription && (
                                                                                <TableRow>
                                                                                    <TableCell
                                                                                        style={{
                                                                                            paddingTop: 0,
                                                                                            borderBottom: showFeeCell ? 'none' : undefined,
                                                                                        }}
                                                                                    />

                                                                                    <TableCell
                                                                                        colSpan={4}
                                                                                        style={{
                                                                                            padding: '0 12px 6px 0',
                                                                                            borderBottom: showFeeCell ? 'none' : undefined,
                                                                                        }}
                                                                                    >
                                                                                        <Typography
                                                                                            sx={{
                                                                                                fontSize: '14px',
                                                                                                fontWeight: 400,
                                                                                                lineHeight: '20px',
                                                                                                letterSpacing: '0.4px',
                                                                                                color: '#848484',
                                                                                            }}
                                                                                        >
                                                                                            {row?.lastDescription}
                                                                                        </Typography>
                                                                                    </TableCell>

                                                                                    {showLineTax && (
                                                                                        <TableCell
                                                                                            style={{
                                                                                                paddingTop: 0,
                                                                                                borderBottom: showFeeCell ? 'none' : undefined,
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </TableRow>
                                                                            )}

                                                                            {showFeeCell && (
                                                                                <TableRow>
                                                                                    <TableCell style={{ padding: '0 12px 6px 16px', fontSize: '16px' }} />
                                                                                    <TableCell style={{ padding: '0 12px 6px 0', fontSize: '16px' }}>
                                                                                        {row?.fee?.name}
                                                                                    </TableCell>
                                                                                    <TableCell style={{ padding: '0 12px', fontSize: '16px' }}>
                                                                                        {row?.totalQty}
                                                                                    </TableCell>
                                                                                    <TableCell style={{ padding: '0 12px', fontSize: '16px' }}>
                                                                                        {NumericEngine.format(row?.feeAmount || 0, {
                                                                                            formatCurrency: true,
                                                                                            trailingZeros: true,
                                                                                        })}
                                                                                    </TableCell>

                                                                                    <TableCell
                                                                                        align={showLineTax ? 'left' : 'right'}
                                                                                        style={{ padding: '0 12px', fontSize: '16px' }}
                                                                                    >
                                                                                        {NumericEngine.format(
                                                                                            NumericEngine.multiply(row?.totalQty || 0, row?.feeAmount || 0),
                                                                                            { formatCurrency: true, trailingZeros: true }
                                                                                        )}
                                                                                    </TableCell>

                                                                                    {showLineTax && (
                                                                                        <TableCell style={{ padding: '0 16px 0 10px', fontSize: '16px' }} />
                                                                                    )}
                                                                                </TableRow>
                                                                            )}
                                                                        </Fragment>
                                                                    );
                                                                })}

                                                            {shouldShowPerServiceSubtotalRow(item, { variant: 'invoice-detail' }) && (
                                                                <TableRow>
                                                                    <TableCell
                                                                        colSpan={4}
                                                                        style={{
                                                                            // borderBottom: 0,
                                                                            background: '#fff',
                                                                            padding: '6px 12px',
                                                                        }}
                                                                    />

                                                                    <TableCell
                                                                        align={showLineTax ? 'left' : 'right'}
                                                                        style={{
                                                                            // borderBottom: 0,
                                                                            background: '#fff',
                                                                            padding: '6px 12px',
                                                                            fontSize: '16px',
                                                                            fontWeight: 400,
                                                                        }}
                                                                    >
                                                                        <Typography fontWeight={400} fontSize={16}>
                                                                            {serviceSubtotal > 0
                                                                                ? NumericEngine.format(serviceSubtotal, {
                                                                                      formatCurrency: true,
                                                                                      trailingZeros: true,
                                                                                  })
                                                                                : '$0.00'}
                                                                        </Typography>
                                                                    </TableCell>

                                                                    {showLineTax && (
                                                                        <TableCell
                                                                            style={{
                                                                                // borderBottom: 0,
                                                                                background: '#fff',
                                                                                padding: '12px',
                                                                            }}
                                                                        />
                                                                    )}
                                                                </TableRow>
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Grid>
                                        </Grid>
                                    </>
                                );
                            })}

                        {/* Fees Table  */}
                        {invoiceData?.fees?.length > 0 && <InvoiceFeesTable invoiceData={invoiceData} />}
                        {invoiceData?.recommendationNotes && <InvoiceRecommandationSection invoiceData={invoiceData} />}

                        {/* Shop Supplies Table  */}
                        {invoiceData &&
                            invoiceData?.shopSupplies?.length > 0 &&
                            invoiceData?.shopSupplies?.map((item, index) => {
                                return <ShopSuppliesTable item={item} index={index} invoiceData={invoiceData} />;
                            })}

                        <Grid container spacing={2} mt={'32px'} justifyContent={'space-between'} alignItems={'flex-start'}>
                            <Grid item xs={12} md={6} lg={6} xl={7}>
                                {invoiceData?.termsAndWarranty?.title && invoiceData?.termsAndWarranty?.description && (
                                    <InvoiceTermsWarranty data={invoiceData} />
                                )}
                            </Grid>
                            <Grid item xs={12} md={6} lg={6} xl={5}>
                                {/* Payment Details Table  */}

                                <InvoicePaymenetDetails invoiceData={invoiceData} isDraft={true} />
                            </Grid>
                        </Grid>

                        {/* Payment Details Table  */}
                    </Grid>
                </Grid>
            </Box>

            <AddInventoryModal
                type={INVENTORY_TYPES.INVOICE}
                showUsedInInvoiceFeild={true}
                FeildLabelName="Added to Invoice"
                isUsedInvoiceQty={inventoryOption?.totalQty}
                isUseLastCost={inventoryOption?.totalQty ? true : false}
                inventoryId={selectedInventoryId}
                batchNumber={batchNumber}
                viewOnly={true}
                openModal={openPartModel}
                closeModal={() => {
                    setOpenpartModel(false);
                    setSelectedInventoryId(null);
                    setBatchNumber(null);
                }}
                onSubmitModal={() => {
                    setOpenpartModel(false);
                    setSelectedInventoryId(null);
                    setBatchNumber(null);
                }}
                showLoader={false}
                setShowLoader={() => {}}
                selectedInventory={[]}
                setSelectedInventory={() => {}}
                fetchApi={(data) => {}}
                ShowAllTabs={false}
                ShowTabIndex={0}
                modalType={'edit'}
            />

            <BatchViewModal
                invoiceId={invoiceId}
                inventoryId={inventoryOption}
                open={openBatchSelectionModal}
                onClose={() => {
                    setBatchSelectionModal(false);
                }}
                onSave={() => {
                    setBatchSelectionModal(false);
                }}
            />

            {openEditCustomer && (
                <Suspense fallback={<CircularProgress />}>
                    <AddCustomerModalV4
                        from="invoice_email"
                        openModal={openEditCustomer}
                        customerId={invoiceData?.customer?.refIdImport || invoiceData?.customer?.refIdUsers || customerId}
                        customerType={invoiceData?.customer?.createdFrom || invoiceData?.customer?.source || SHOP_CREATED}
                        onClose={() => {
                            setOpenEditCustomer(false);
                        }}
                        onSubmitModal={(updatedCustomer) => {
                            setOpenEditCustomer(false);

                            // update local customer state if needed
                            if (updatedCustomer) {
                                setCustomerData?.(updatedCustomer);
                            }

                            // continue send flow
                            handleSend();
                        }}
                    />
                </Suspense>
            )}

            <CommonDialog
                loader={deleteLoader}
                open={isDeleteModal}
                onClose={() => {
                    setIsDeleteModal(false);
                }}
                onSave={async () => {
                    setDeleteLoader(true);
                    let data = await dispatch(deleteInvoice(invoiceId));

                    if (data) {
                        queryClient.invalidateQueries({ queryKey: businessInvoicesKeys.all, refetchType: 'all' });
                        queryClient.invalidateQueries({ queryKey: [...reviewRequestKeys.all, 'quickReviewUsers'], refetchType: 'all' });
                        navigate('/invoice/drafts');
                    }
                    setIsDeleteModal(false);
                    setDeleteLoader(true);
                }}
                dialogTitle={'Delete Invoice?'}
                dialogSubtitle={`Are you sure you want to delete Invoice ${invoiceData?.invoiceNumber || ''}? This action cannot be undone.`}
            />
            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />
            <Dialog open={showLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default InvoiceDraft;
